<div class="panel">
  <div class="toolbar">
    <div class="field">
      <label for="daily-results-through">{{ listDays }} days through</label>
      <p-datepicker
        inputId="daily-results-through"
        appendTo="body"
        dateFormat="yy-mm-dd"
        [showIcon]="true"
        [maxDate]="latestDay"
        [ngModel]="through()"
        (ngModelChange)="through.set($event)"
      ></p-datepicker>
    </div>
    <p-button label="Reload" icon="pi pi-refresh" [text]="true" [disabled]="!through()" (onClick)="reload()"></p-button>
  </div>

  @if (!through()) {
    <p class="muted">Choose the last local day to list.</p>
  } @else if (daysResource.error()) {
    <p-message severity="error" text="Failed to load the daily results from NourishmentEngine."></p-message>
  } @else if (daysResource.value(); as days) {
    <p-table
      dataKey="date"
      [value]="days"
      [rowTrackBy]="trackDay"
      [expandedRowKeys]="expandedDays()"
      [loading]="daysResource.isLoading()"
      [tableStyle]="{ 'min-width': '64rem' }"
    >
      <ng-template #header>
        <tr>
          <th class="toggle-cell"></th>
          <th>Local day</th>
          <th>Status</th>
          <th>Final score</th>
          <th>Band</th>
          <th>Preliminary</th>
          <th>Energy balance</th>
          <th>Plan</th>
          <th>Algorithm</th>
          <th>Scored at</th>
        </tr>
      </ng-template>
      <ng-template #body let-day let-expanded="expanded">
        <tr>
          @if (day.result; as result) {
            <td class="toggle-cell">
              @if (hasBreakdown(result)) {
                <p-button
                  size="small"
                  [pRowToggler]="day"
                  [text]="true"
                  [rounded]="true"
                  [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                  [ariaLabel]="(expanded ? 'Hide' : 'Show') + ' the breakdown for ' + day.date"
                ></p-button>
              }
            </td>
            <td class="stamp-cell">{{ day.date }}</td>
            <td><p-tag [value]="result.status" [severity]="severity(result.status)"></p-tag></td>
            <td>{{ result.finalScore | number: '1.3-3' }}</td>
            <td class="stamp-cell">{{ band(result) }}</td>
            <td>{{ result.preliminaryScore | number: '1.3-3' }}</td>
            <td>{{ result.energyBalance | number: '1.2-3' }} × {{ result.energyMultiplier | number: '1.1-2' }}</td>
            <td>{{ result.planType }} {{ result.planEffectiveDate ?? '' }}</td>
            <td>v{{ result.algorithmVersion }}</td>
            <td class="stamp-cell">{{ result.scoredAtUtc ? utcStamp(result.scoredAtUtc) : '' }}</td>
          } @else {
            <td class="toggle-cell"></td>
            <td class="stamp-cell">{{ day.date }}</td>
            <td><p-tag value="Not scored" severity="secondary"></p-tag></td>
            <td class="muted" colspan="7">
              No stored result. The day closer marks a day after local midnight; use Rescore to force it.
            </td>
          }
        </tr>
      </ng-template>
      <ng-template #expandedrow let-day>
        @if (day.result; as result) {
          <tr>
            <td class="breakdown-cell" colspan="10">
              <app-daily-result-detail [result]="result" [showSummary]="false"></app-daily-result-detail>
            </td>
          </tr>
        }
      </ng-template>
    </p-table>
  } @else {
    <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
  }

  <div class="section">
    <h4>Rescore</h4>
    <p class="muted">
      Leave both dates empty to rescore the look-back window, which the engine reckons in each household's local days. A
      range is taken as given and may cover at most {{ maxRescoreRangeDays }} days.
    </p>
    <div class="toolbar">
      <div class="field">
        <label for="rescore-from">From</label>
        <p-datepicker
          inputId="rescore-from"
          appendTo="body"
          dateFormat="yy-mm-dd"
          placeholder="Look-back window"
          [showIcon]="true"
          [showClear]="true"
          [maxDate]="latestDay"
          [ngModel]="rescoreFrom()"
          (ngModelChange)="rescoreFrom.set($event)"
        ></p-datepicker>
      </div>
      <div class="field">
        <label for="rescore-to">To</label>
        <p-datepicker
          inputId="rescore-to"
          appendTo="body"
          dateFormat="yy-mm-dd"
          placeholder="Look-back window"
          [showIcon]="true"
          [showClear]="true"
          [maxDate]="latestDay"
          [ngModel]="rescoreTo()"
          (ngModelChange)="rescoreTo.set($event)"
        ></p-datepicker>
      </div>
      <p-button
        label="Rescore"
        icon="pi pi-replay"
        severity="warn"
        [outlined]="true"
        [loading]="rescoring()"
        (onClick)="onRescore()"
      ></p-button>
    </div>
    @if (lastRescore(); as last) {
      <p class="muted">
        @if (last.marked === 0) {
          No days were marked.
        } @else if (last.range; as range) {
          Marked {{ last.marked }} day(s) from {{ range.from }} to {{ range.to }}.
        } @else {
          Marked {{ last.marked }} day(s) in the look-back window.
        }
        @switch (last.progress) {
          @case ('waiting') {
            Waiting for the sweep, {{ last.queuedDays }} day(s) still queued.
          }
          @case ('queued') {
            {{ last.queuedDays }} day(s) are still queued.
            @if (last.failureReason; as reason) {
              Last failure: {{ reason }}
            }
          }
          @case ('unknown') {
            The queue could not say whether they were scored. Reload the list later.
          }
          @case ('scored') {
            @if (last.marked > 0) {
              The sweep has scored them and the list is current.
            }
          }
        }
      </p>
    }
  </div>

  <div class="section">
    <h4>Simulate a day</h4>
    <p class="muted">
      Scores the quantities below against this Youtopian's current targets with the live daily options. Nothing is
      stored.
    </p>
    <p-table [value]="simulationRows()" [rowTrackBy]="trackSimulationRow" [tableStyle]="{ 'min-width': '40rem' }">
      <ng-template #header>
        <tr>
          <th>Nutrient id</th>
          <th>Quantity</th>
          <th></th>
        </tr>
      </ng-template>
      <ng-template #body let-row>
        <tr>
          <td>
            <input
              pInputText
              class="nutrient-input"
              placeholder="MaiDy nutrient id"
              [invalid]="hasInvalidNutrientId(row)"
              [ngModel]="row.nutrientId"
              (ngModelChange)="updateSimulationRow(row.key, { nutrientId: $event })"
            />
            @if (hasInvalidNutrientId(row)) {
              <small class="field-error">A nutrient id is a GUID.</small>
            }
          </td>
          <td>
            <p-inputnumber
              [min]="0"
              [maxFractionDigits]="4"
              [ngModel]="row.quantity"
              (ngModelChange)="updateSimulationRow(row.key, { quantity: $event })"
            ></p-inputnumber>
          </td>
          <td class="actions-cell">
            <p-button
              icon="pi pi-times"
              severity="danger"
              size="small"
              [text]="true"
              (onClick)="removeSimulationRow(row.key)"
            ></p-button>
          </td>
        </tr>
      </ng-template>
    </p-table>
    <div class="toolbar">
      <p-button label="Add nutrient" icon="pi pi-plus" [text]="true" (onClick)="addSimulationRow()"></p-button>
      <p-button
        label="Score day"
        icon="pi pi-play"
        [disabled]="!canSimulate()"
        [loading]="simulating()"
        (onClick)="simulate()"
      ></p-button>
    </div>

    @if (simulationError(); as error) {
      <p-message severity="error" [text]="error"></p-message>
    }
    @if (simulation(); as simulated) {
      <app-daily-result-detail [result]="simulated"></app-daily-result-detail>
    }
  </div>
</div>
